import { KeyboardEvent, useEffect, useState, FC } from 'react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { IconButton, SpinningLoader, Text, useTooltip } from '@frontend/design-system';
import type { SendingMediaItem } from '../types';

type PreviewImageProps = {
  image: SendingMediaItem;
  onDelete: () => void;
  onPreview: (src: string) => void;
  inPopout?: boolean;
  removeImageTrackingId?: string;
};

export const PreviewImage: FC<PreviewImageProps> = ({
  image,
  onDelete,
  onPreview,
  inPopout,
  removeImageTrackingId,
}) => {
  const { t } = useTranslation('inbox');
  const { Tooltip, triggerProps, tooltipProps } = useTooltip({ placement: 'top' });
  const [imageError, setImageError] = useState(false);
  const [focus, setFocus] = useState<'image' | 'button' | 'none'>('none');
  const isUploading = image.uploading || !image.mediaObj;
  const hasError = image.hasError;
  const [fileSrc, setFileSrc] = useState<string>();
  const src = image.previewSrc || fileSrc;
  const name = image.file?.name || image.id;
  const fileExtension = image.file?.type.split('/').pop()?.toUpperCase();

  const handlePreview = () => {
    if (image.mediaObj?.url) {
      onPreview(image.mediaObj.url);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      handlePreview();
    }
  };

  useEffect(() => {
    if (!image.file) return;
    const reader = new FileReader();

    reader.onload = () => setFileSrc(reader.result as string);
    reader.readAsDataURL(image.file);

    return () => {
      setFileSrc(undefined);
    };
  }, [image.file]);

  if (!src) return null;

  return (
    <>
      <li
        css={{
          listStyleType: 'none',
          height: '100%',
          maxWidth: inPopout ? '40%' : '25%',
          flexShrink: 0,
          cursor: isUploading ? 'auto' : 'pointer',
          position: 'relative',
          borderRadius: theme.borderRadius.medium,
          ':focus': {
            outline: 'none',
          },
        }}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onFocusCapture={() => {
          setFocus('image');
        }}
        onBlurCapture={() => {
          setFocus('none');
        }}
      >
        {isUploading && (
          <div
            css={{
              transition: 'background-color 300ms ease-in-out',
              zIndex: 1,
              maxHeight: '100%',
              height: 'auto',
              width: 'auto',
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              backgroundColor: `rgba(255, 255, 255, 0.5)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <SpinningLoader />
          </div>
        )}
        {!isUploading && (
          <IconButton
            css={{
              position: 'absolute',
              top: theme.spacing(1),
              right: theme.spacing(1),
              backgroundColor: hasError ? 'transparent' : theme.colors.white,
              padding: inPopout ? theme.spacing(0.5) : undefined,
            }}
            aria-label={
              hasError
                ? t('Issue uploading image {{name}}. Click to remove.', { name })
                : t('Remove image attachment {{name}}.', { name })
            }
            label={hasError ? t('Issue uploading image. Click to remove.') : t('Remove image attachment.')}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              !isUploading && onDelete();
            }}
            showLabelOnHover={hasError}
            onFocusCapture={() => setFocus('button')}
            size={inPopout ? 'xsmall' : undefined}
            {...(removeImageTrackingId && { trackingId: removeImageTrackingId })}
          >
            {hasError ? <Icon name='alert' color='error' /> : <Icon name='x' size={inPopout ? 16 : undefined} />}
          </IconButton>
        )}
        {!imageError ? (
          <img
            css={[
              {
                maxHeight: '100%',
                maxWidth: '100%',
                height: 'auto',
                width: 'auto',
                borderRadius: theme.borderRadius.medium,
                ':hover': {
                  outline: `2px solid ${theme.colors.primary50}`,
                },
              },
              focus === 'image' && {
                outline: `2px solid ${theme.colors.primary50}`,
              },
            ]}
            src={src}
            onClick={() => (image.mediaObj?.url ? handlePreview() : undefined)}
            onError={() => setImageError(true)}
          />
        ) : (
          <figure
            css={{
              height: '100%',
              width: '90px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: theme.colors.white,
              color: theme.colors.black,
              borderRadius: theme.borderRadius.medium,
              padding: theme.spacing(1),
              ':hover': {
                outline: `2px solid ${theme.colors.primary50}`,
              },
            }}
            {...triggerProps}
          >
            <Icon name='document' size={24} />
            <Text color='subdued'>
              <figcaption>{fileExtension}</figcaption>
            </Text>
            <Tooltip {...tooltipProps}>
              <Text size='medium' color='white'>
                {name}
              </Text>
            </Tooltip>
          </figure>
        )}
      </li>
    </>
  );
};
